import { Card, makeStyles } from '@material-ui/core'
import type { TileItem } from '@paintscout/ui'
import { ActionButton, Collapse, Grid, HtmlContent, IconButton, Typography } from '@paintscout/ui'
import classNames from 'classnames'
import React, { useState } from 'react'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'

const useStyles = makeStyles((theme) => ({
  tile: {
    padding: theme.spacing(2),
    marginBottom: 8,
    borderRadius: theme.borderRadius.md,
    borderLeft: '8px solid'
  },
  dashed: {
    position: 'relative',
    overflow: 'hidden',
    backgroundImage: `
    repeating-linear-gradient(
      135deg,
      ${theme.paintscout.palette.brand.Neutral.P98},
      ${theme.paintscout.palette.brand.Neutral.P98} 10px,
      ${theme.paintscout.palette.brand.Neutral.P94} 10px,
      ${theme.paintscout.palette.brand.Neutral.P94} 12px
    )
  `,
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: theme.borderRadius.md
    }
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  subLabel: {
    whiteSpace: 'pre-wrap'
  },
  description: {
    marginTop: theme.spacing(1),
    whiteSpace: 'pre-wrap'
  },
  expandIcon: {
    padding: 0
  }
}))

export interface EventTileItem extends TileItem {
  isIntegrationEvent?: boolean
  address?: string
  description?: string
}

export interface EventCardProps {
  item: EventTileItem
  onClick: () => void
  onActionClick: (actionName: string) => void
}

export function EventCard({ item, onClick, onActionClick }: EventCardProps) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false)
  const showExpandIcon = item.isIntegrationEvent && item.description
  return (
    <Card
      key={item.key}
      className={classNames({
        [classes.tile]: true,
        [classes.dashed]: item.dashed
      })}
      style={{
        borderLeftColor: item.borderColor || undefined,
        cursor: item.isIntegrationEvent ? 'default' : 'pointer'
      }}
      onClick={onClick}
    >
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item xs={showExpandIcon ? 12 : true}>
          <Typography variant="body1" className={classes.label}>
            {item.label}
            {showExpandIcon && (
              <IconButton className={classes.expandIcon}>
                {expanded ? (
                  <ExpandLessIcon onClick={() => setExpanded(false)} />
                ) : (
                  <ExpandMoreIcon onClick={() => setExpanded(true)} />
                )}
              </IconButton>
            )}
          </Typography>
          <Typography variant="subtitle1" className={classes.subLabel}>
            {item.sublabel}
          </Typography>
        </Grid>
        <Grid item>
          {!item.isIntegrationEvent && (
            <ActionButton
              style={{ padding: 0 }}
              actions={[
                {
                  key: 'view-deal',
                  label: 'View Deal'
                },
                item.address && {
                  key: 'open-maps',
                  label: 'Open Maps'
                },
                {
                  key: 'edit',
                  label: 'Edit'
                },
                {
                  key: 'cancel',
                  label: 'Cancel'
                }
              ].filter(Boolean)}
              onActionClick={(_event, actionKey, _payload) => {
                onActionClick(actionKey)
              }}
            />
          )}
        </Grid>
        {item.isIntegrationEvent && item.description && (
          <Collapse show={expanded}>
            <Typography variant="subtitle1" className={classes.description}>
              <HtmlContent content={`Description:\n${item.description}`} />
            </Typography>
          </Collapse>
        )}
      </Grid>
    </Card>
  )
}
